/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import input from "../../../../assets/styles/components/input.module.css";
import select from "../../../../assets/styles/components/select.module.css";
import {
  fetchFinancialEntryByUid,
  fetchPaymentTypes,
  registerOrEditFinancialEntry,
} from "../../../../services/financial";
import { loadAllClients } from "../../../../services/customers";
import type { IFinancialEntry } from "../../../../types/financial";
import type { IRequest } from "../../../../types/request";
import Button from "../../../../components/Button/Button";
import SelectChartOfAccounts from "../CashFlow/SelectChartOfAccounts";
import SelectCostCenter from "../CashFlow/SelectCostCenter";
import SelectFinanceAccount from "../CashFlow/SelectFinanceAccount";
import InputSuspense from "../../../../components/InputSuspense/InputSuspense";
import styles from "./IncomingPaymentsAdd.module.css";

// Interfaces locais
interface ClienteSelect {
  uid_cliente: string;
  nome?: string;
  nome_fantasia?: string;
}

interface PaymentTypeSelect {
  uid_tipo_pagamento: string;
  titulo: string;
  ordem?: number;
}

type Tipo = "pagar" | "receber";
type Origem = "pedido" | "venda" | "compra" | "manual" | "mensalidade";
type Status = "aberto" | "pago" | "atrasado" | "cancelado";

export interface FinancialEntryFormData {
  uid_financeiro_lancamento?: string;
  tipo: Tipo;
  numero_documento: string | null;
  descricao: string;
  codigo_estabelecimento?: number;
  uid_cliente?: string | null;
  uid_fornecedor?: string | null;
  uid_plano_contas: string | null;
  uid_centro_custo?: string | null;
  uid_conta_financeira?: string | null;
  uid_pedido?: string | null;
  origem: Origem;
  valor: number;
  desconto: number;
  juros: number;
  valor_total: number;
  valor_parcela: number;
  parcela: number;
  total_parcelas: number;
  data_emissao: string | null;
  data_competencia?: string | null;
  data_vencimento: string | null;
  data_pagamento?: string | null;
  status: Status;
  uid_tipo_pagamento?: string | null;
  observacao?: string | null;
}

interface FormIncomingPaymentsProps {
  uid?: string;
  isEdit: boolean;
  handleVisible: (key: string, value: boolean) => void;
  onSuccessSave: (newUid: string) => void;
  entryToEdit?: IFinancialEntry;
  entryToCopy?: IFinancialEntry;
  requestData?: IRequest;
}

const FormIncomingPayments = ({
  uid,
  isEdit,
  handleVisible,
  onSuccessSave,
  entryToEdit,
  entryToCopy,
  requestData,
}: FormIncomingPaymentsProps) => {
  const queryClient = useQueryClient();

  // Estados locais do formulário
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [isActiveClient, setIsActiveClient] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [originalValue, setOriginalValue] =
    useState<FinancialEntryFormData | null>(null);

  // Estados de cálculo
  const [valorBruto, setValorBruto] = useState(0);
  const [valorDesconto, setValorDesconto] = useState(0);
  const [valorJuros, setValorJuros] = useState(0);
  const [valorTotalCalculado, setValorTotalCalculado] = useState(0);

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
    getValues,
  } = useForm<FinancialEntryFormData>({
    defaultValues: {
      tipo: "receber",
      origem: "manual",
      status: "aberto",
      parcela: 1,
      total_parcelas: 1,
      valor: 0,
      desconto: 0,
      juros: 0,
      valor_total: 0,
      valor_parcela: 0,
      uid_cliente: null,
      uid_fornecedor: null,
      uid_tipo_pagamento: "",
      data_pagamento: null,
      observacao: null,
      uid_plano_contas: null,
      uid_centro_custo: "",
      uid_conta_financeira: null,
      codigo_estabelecimento: undefined,
      data_competencia: null,
      data_emissao: new Date().toISOString().split("T")[0],
    },
  });

  const totalParcelas = watch("total_parcelas");
  const uidTipoPagamentoWatch = watch("uid_tipo_pagamento");
  const uidPlanoContasWatch = watch("uid_plano_contas");
  const uidCentroCustoWatch = watch("uid_centro_custo");
  const uidContaFinanceiraWatch = watch("uid_conta_financeira");
  const watchedStatus = watch("status");
  const watchedVencimento = watch("data_vencimento");

  // Queries
  const { data: fetchedEntry, isLoading: isLoadingEntry } = useQuery({
    queryKey: ["financialEntry", uid],
    queryFn: () =>
      fetchFinancialEntryByUid({
        uid: uid!,
        type: "receber",
      }),
    enabled: !!uid && !entryToEdit && !entryToCopy && !requestData,
  });

  const {
    data: clientsData,
    isLoading: isLoadingClients,
    error: fetchErrorClients,
  } = useQuery({
    queryKey: ["allClientsForSelect", clientSearchTerm],
    queryFn: () => loadAllClients(clientSearchTerm, 1),
  });

  const { data: paymentTypesData, isLoading: isLoadingPaymentTypes } = useQuery<
    PaymentTypeSelect[],
    Error
  >({
    queryKey: ["paymentTypesForSelect"],
    queryFn: fetchPaymentTypes,
  });

  const clientsForSelect: ClienteSelect[] = useMemo(() => {
    return clientsData?.data?.clients || [];
  }, [clientsData]);

  const paymentTypesList: PaymentTypeSelect[] = paymentTypesData || [];

  // Mutation
  const { mutateAsync, isPending } = useMutation({
    mutationFn: registerOrEditFinancialEntry,
    onSuccess: (response: any) => {
      toast.success(response.message);

      if (response?.data?.uid_financeiro_lancamento) {
        onSuccessSave(response.data.uid_financeiro_lancamento);
      }

      queryClient.invalidateQueries({ queryKey: ["financialEntries"] });
      queryClient.invalidateQueries({ queryKey: ["financialEntry", uid] });

      handleVisible("isEdit", false);
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  // Efeitos e Lógica de Negócio
  useEffect(() => {
    register("uid_plano_contas");
    register("uid_centro_custo");
    register("uid_pedido");
    register("uid_conta_financeira");
    register("valor", { required: "O valor é obrigatório", min: { value: 0.01, message: "O valor deve ser maior que zero" } });
  }, [register]);

  useEffect(() => {
    const total = valorBruto - valorDesconto + valorJuros;
    const finalTotal = total > 0 ? total : 0;
    setValorTotalCalculado(finalTotal);
    setValue("valor_total", finalTotal);
  }, [valorBruto, valorDesconto, valorJuros, setValue]);

  useEffect(() => {
    const numParcelas = totalParcelas || 1;
    const calcValorParcela =
      numParcelas > 0 ? valorTotalCalculado / numParcelas : valorTotalCalculado;
    setValue("valor_parcela", calcValorParcela);
  }, [valorTotalCalculado, totalParcelas, setValue]);

  const resetForm = useCallback(() => {
    reset({
      tipo: "receber",
      origem: "manual",
      status: "aberto",
      parcela: 1,
      total_parcelas: 1,
      valor: 0,
      desconto: 0,
      juros: 0,
      valor_total: 0,
      valor_parcela: 0,
      uid_cliente: null,
      uid_fornecedor: null,
      uid_tipo_pagamento: "",
      observacao: null,
      data_emissao: new Date().toISOString().split("T")[0],
      data_vencimento: null,
      data_pagamento: null,
      uid_plano_contas: null,
      uid_centro_custo: "",
      uid_conta_financeira: null,
      codigo_estabelecimento: undefined,
    });

    setValorBruto(0);
    setValorDesconto(0);
    setValorJuros(0);
    setSelectedClientName("");
  }, [reset]);

  // Inicialização do Formulário
  useEffect(() => {
    const dataToUse = entryToEdit || fetchedEntry;

    if (dataToUse && !isInitialized) {
      const valuesToReset = Object.entries(dataToUse).reduce(
        (acc, [key, value]) => {
          let formattedValue: any = value;

          if (
            [
              "data_emissao",
              "data_vencimento",
              "data_pagamento",
              "data_competencia",
            ].includes(key)
          ) {
            formattedValue = value ? String(value).split("T")[0] : null;
          }

          if (
            [
              "valor",
              "valor_total",
              "desconto",
              "juros",
              "valor_parcela",
            ].includes(key)
          ) {
            formattedValue = value ? Number(value) : 0;
          }

          // @ts-ignore
          acc[key] = formattedValue;
          return acc;
        },
        {} as FinancialEntryFormData,
      );

      valuesToReset.tipo = "receber";
      setValorBruto(Number(dataToUse.valor || dataToUse.valor_total || 0));
      setValorDesconto(Number(dataToUse.desconto || 0));
      setValorJuros(Number(dataToUse.juros || 0));

      if (dataToUse.uid_cliente) {
        const clientFound = clientsForSelect?.find(
          (c) => c.uid_cliente === dataToUse.uid_cliente,
        );
        const clientName =
          clientFound?.nome_fantasia || clientFound?.nome || (dataToUse.cliente as any)?.nome_fantasia || dataToUse.cliente?.nome || "Cliente selecionado";
        setSelectedClientName(clientName);
      } else {
        setSelectedClientName("");
      }

      reset(valuesToReset);

      const paymentUid =
        dataToUse.tipo_pagamento?.uid_tipo_pagamento ??
        dataToUse.uid_tipo_pagamento ??
        "";
      setValue("uid_tipo_pagamento", paymentUid);

      setIsInitialized(true);
    } else if (entryToCopy && !isInitialized) {
      // Caso de cópia
      const valuesToReset = Object.entries(entryToCopy).reduce(
        (acc, [key, value]) => {
          let formattedValue: any = value;
          if (
            [
              "data_emissao",
              "data_vencimento",
              "data_pagamento",
              "data_competencia",
            ].includes(key)
          ) {
            formattedValue = value ? String(value).split("T")[0] : null;
          }
          if (
            [
              "valor",
              "valor_total",
              "desconto",
              "juros",
              "valor_parcela",
            ].includes(key)
          ) {
            formattedValue = value ? Number(value) : 0;
          }
          // @ts-ignore
          acc[key] = formattedValue;
          return acc;
        },
        {} as FinancialEntryFormData,
      );

      valuesToReset.tipo = "receber";
      setValorBruto(Number(entryToCopy.valor || entryToCopy.valor_total || 0));

      if (entryToCopy.uid_cliente) {
        const clientFound = clientsForSelect.find(
          (c) => c.uid_cliente === entryToCopy.uid_cliente,
        );
        setSelectedClientName(
          clientFound?.nome_fantasia || clientFound?.nome ||
            (entryToCopy.cliente as any)?.nome_fantasia || entryToCopy.cliente?.nome ||
            "Cliente selecionado",
        );
      }

      reset(valuesToReset);

      const paymentUid =
        entryToCopy.tipo_pagamento?.uid_tipo_pagamento ??
        entryToCopy.uid_tipo_pagamento ??
        "";
      setValue("uid_tipo_pagamento", paymentUid);

      setIsInitialized(true);
    } else if (requestData && !isInitialized) {
      // Caso vindo de um Pedido/Request
      setValorBruto(requestData.valor_total || 0);
      setSelectedClientName(requestData.cliente?.nome || "");
      reset({
        tipo: "receber",
        origem: "pedido",
        status: "aberto",
        uid_pedido: requestData.uid_pedido,
        uid_cliente: requestData.uid_cliente,
        valor: requestData.valor_total,
        valor_total: requestData.valor_total,
        parcela: 1,
        total_parcelas: 1,
        data_emissao: new Date().toISOString().split("T")[0],
      });
      setIsInitialized(true);
    } else if (
      !isLoadingEntry &&
      !uid &&
      !entryToCopy &&
      !requestData &&
      !isInitialized
    ) {
      resetForm();
      setIsInitialized(true);
    }
  }, [
    entryToEdit,
    fetchedEntry,
    entryToCopy,
    requestData,
    isInitialized,
    reset,
    setValue,
    clientsForSelect,
    isLoadingEntry,
    uid,
    resetForm,
  ]);

  // Handlers de Clientes
  const handleSelectClient = useCallback(
    (nome: string, uid: string) => {
      setValue("uid_cliente", uid, { shouldValidate: true });
      setSelectedClientName(nome);
      setClientSearchTerm("");
      setIsActiveClient(false);
    },
    [setValue],
  );

  const handleClientInput = (value: string) => {
    setClientSearchTerm(value);
    setSelectedClientName(value);
    setIsActiveClient(true);

    if (value === "") {
      setValue("uid_cliente", null);
    }
  };

  const clientOptionsMemo = useMemo(() => {
    if (isLoadingClients)
      return <p className={styles.dropDownLoading}>Carregando...</p>;
    if (fetchErrorClients)
      return <p className={styles.dropDownError}>Erro ao carregar clientes.</p>;
    if (!clientsForSelect || clientsForSelect.length === 0) {
      return (
        <p className={styles.dropDownNoResults}>Nenhum cliente encontrado.</p>
      );
    }
    return clientsForSelect.map((item: ClienteSelect) => (
      <p
        key={item.uid_cliente}
        className={styles.dropDownItem}
        onClick={() => handleSelectClient(item.nome_fantasia || item.nome || "", item.uid_cliente)}
      >
        {item.nome_fantasia || item.nome}
      </p>
    ));
  }, [
    clientsForSelect,
    isLoadingClients,
    fetchErrorClients,
    handleSelectClient,
  ]);

  const onSubmit: SubmitHandler<FinancialEntryFormData> = async (data) => {
    try {
      const formattedData = {
        ...data,
        tipo: "receber" as Tipo,
        origem: "manual" as Origem,
        numero_documento: data.numero_documento,
        valor: valorBruto,
        desconto: valorDesconto,
        juros: valorJuros,
        valor_total: valorTotalCalculado,
        valor_parcela: valorTotalCalculado,
        parcela: 1,
        total_parcelas: 1,
        data_emissao: data.data_emissao ? data.data_emissao : null,
        data_competencia: null,
        data_vencimento: data.data_vencimento ? data.data_vencimento : null,
        data_pagamento: data.data_pagamento ? data.data_pagamento : null,
        uid_cliente: data.uid_cliente || null,
        uid_fornecedor: data.uid_fornecedor || null,
        uid_tipo_pagamento: data.uid_tipo_pagamento || null,
        uid_centro_custo: data.uid_centro_custo || null,
        uid_pedido: data.uid_pedido || null,
        observacao: data.observacao || null,
        codigo_estabelecimento: isEdit ? data.codigo_estabelecimento : 0,
      };

      if (uid) {
        formattedData.uid_financeiro_lancamento = uid;
      }

      // @ts-ignore
      await mutateAsync(formattedData);
    } catch (error) {
      console.error("Erro no formulário:", error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      setOriginalValue(getValues());
    }
  }, [isEdit]);

  // ── helpers de formatação ────────────────────────────────────────────────
  const formatBRL = (v: number) =>
    v > 0 ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—";
  const formatDateDisplay = (d: string | null | undefined) => {
    if (!d) return "—";
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  // ── estilos inline ────────────────────────────────────────────────────────
  const labelStyle: React.CSSProperties = { fontSize: "0.72rem", color: "#888", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block" };
  const labelEditStyle: React.CSSProperties = { ...labelStyle, color: "#00b37e" };
  const labelErrStyle: React.CSSProperties = { ...labelStyle, color: "#e74c3c" };
  const inputEdit: React.CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #00b37e", outline: "none", fontSize: "0.9rem", background: "#f0fff8", boxSizing: "border-box" as const };
  const inputErr: React.CSSProperties = { ...inputEdit, border: "1px solid #e74c3c", background: "#fff5f5" };
  const inputDis: React.CSSProperties = { ...inputEdit, background: "#f0f0f0", color: "#888", cursor: "not-allowed" };
  const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "20px 24px", padding: "16px 4px" };
  const divider: React.CSSProperties = { borderTop: "1px solid #eee", margin: "0 0 20px" };
  const errSpan: React.CSSProperties = { color: "#e74c3c", fontSize: "0.78rem", marginTop: 3, display: "block" };
  const valStyle: React.CSSProperties = { fontWeight: 500, fontSize: "0.95rem", color: "#222" };

  const ViewField = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div><span style={labelStyle}>{label}</span><div style={valStyle}>{value || "—"}</div></div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>

      {/* ── Seção 1: Identificação ──────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div style={grid}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={errors?.descricao ? labelErrStyle : labelEditStyle}>Descrição *</label>
              <input style={errors?.descricao ? inputErr : inputEdit} type="text"
                {...register("descricao", { required: "A descrição é obrigatória" })} placeholder="Descrição do lançamento" />
              {errors?.descricao && <span style={errSpan}>⚠️ {errors.descricao.message}</span>}
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <InputSuspense label="Cliente" placeholder="Pesquisar cliente..." value={selectedClientName}
                onChange={(e) => handleClientInput(e.target.value)} isActiveDropDown={isActiveClient}
                setIsActiveDropDown={setIsActiveClient} option={clientOptionsMemo} name="uid_cliente_input" readOnly={false} />
            </div>
            <div>
              <label style={labelEditStyle}>Número do documento</label>
              <input style={inputEdit} type="text" {...register("numero_documento")} placeholder="Campo livre" />
            </div>
            <div>
              <label style={errors?.uid_tipo_pagamento ? labelErrStyle : labelEditStyle}>Tipo de Pagamento *</label>
              <select style={errors?.uid_tipo_pagamento ? inputErr : inputEdit}
                {...register("uid_tipo_pagamento", { required: "Tipo de pagamento é obrigatório" })}
                value={uidTipoPagamentoWatch || ""}
                onChange={(e) => setValue("uid_tipo_pagamento", e.target.value, { shouldValidate: true })}
                disabled={isLoadingPaymentTypes}>
                <option value="">Selecione</option>
                {paymentTypesList.map((tp) => <option key={tp.uid_tipo_pagamento} value={tp.uid_tipo_pagamento}>{tp.titulo}</option>)}
              </select>
              {errors?.uid_tipo_pagamento && <span style={errSpan}>⚠️ {errors.uid_tipo_pagamento.message}</span>}
            </div>
          </div>
        ) : (
          <div style={grid}>
            <div style={{ gridColumn: "1 / -1" }}><ViewField label="Descrição" value={watch("descricao")} /></div>
            <ViewField label="Cliente" value={selectedClientName || "—"} />
            <ViewField label="Número do documento" value={watch("numero_documento") || "—"} />
            <ViewField label="Tipo de Pagamento" value={paymentTypesList.find(p => p.uid_tipo_pagamento === uidTipoPagamentoWatch)?.titulo || "—"} />
          </div>
        )}
      </div>

      <div style={divider} />

      {/* ── Seção 2: Classificação ──────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <div style={grid}>
          <div><SelectFinanceAccount value={uidContaFinanceiraWatch || ""} handleChangeAccount={(v) => setValue("uid_conta_financeira", v, { shouldValidate: true, shouldDirty: true })} disabled={!isEdit} /></div>
          <div><SelectChartOfAccounts value={uidPlanoContasWatch} handleChangeChartOfAccount={(v) => setValue("uid_plano_contas", v, { shouldValidate: true, shouldDirty: true })} type="receita" disabled={!isEdit} /></div>
          <div><SelectCostCenter value={uidCentroCustoWatch || ""} handleChangeCostCenter={(v) => setValue("uid_centro_custo", v, { shouldValidate: true, shouldDirty: true })} disabled={!isEdit} /></div>
        </div>
      </div>

      <div style={divider} />

      {/* ── Seção 3: Valores ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div style={grid}>
            <div>
              <label style={errors?.valor ? labelErrStyle : labelEditStyle}>Valor *</label>
              <NumericFormat className={input.input} style={errors?.valor ? { borderColor: "#e74c3c", background: "#fff5f5" } : { borderColor: "#00b37e", background: "#f0fff8" }}
                thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2} fixedDecimalScale value={valorBruto}
                onFocus={(e) => e.target.select()}
                onValueChange={(v) => { setValorBruto(v.floatValue ?? 0); setValue("valor", v.floatValue ?? 0, { shouldValidate: true }); }} placeholder="R$ 0,00" />
              {errors?.valor && <span style={errSpan}>⚠️ {errors.valor.message}</span>}
            </div>
            <div>
              <label style={labelEditStyle}>Desconto</label>
              <NumericFormat className={input.input} style={{ borderColor: "#00b37e", background: "#f0fff8" }}
                thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2} fixedDecimalScale value={valorDesconto}
                onFocus={(e) => e.target.select()} onValueChange={(v) => { setValorDesconto(v.floatValue ?? 0); setValue("desconto", v.floatValue ?? 0); }} placeholder="R$ 0,00" />
            </div>
            <div>
              <label style={labelEditStyle}>Juros</label>
              <NumericFormat className={input.input} style={{ borderColor: "#00b37e", background: "#f0fff8" }}
                thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2} fixedDecimalScale value={valorJuros}
                onFocus={(e) => e.target.select()} onValueChange={(v) => { setValorJuros(v.floatValue ?? 0); setValue("juros", v.floatValue ?? 0); }} placeholder="R$ 0,00" />
            </div>
            <div>
              <label style={labelStyle}>Total Líquido</label>
              <NumericFormat className={input.input} style={{ background: "#f0f0f0", color: "#888", cursor: "not-allowed", fontWeight: 700 }}
                thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2} fixedDecimalScale value={valorTotalCalculado} readOnly />
            </div>
          </div>
        ) : (
          <div style={grid}>
            <ViewField label="Valor" value={formatBRL(valorBruto)} />
            <ViewField label="Desconto" value={valorDesconto > 0 ? formatBRL(valorDesconto) : "—"} />
            <ViewField label="Juros" value={valorJuros > 0 ? formatBRL(valorJuros) : "—"} />
            <ViewField label="Total Líquido" value={<strong>{formatBRL(valorTotalCalculado)}</strong>} />
          </div>
        )}
      </div>

      <div style={divider} />

      {/* ── Seção 4: Datas ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div style={grid}>
            <div>
              <label style={labelEditStyle}>Emissão *</label>
              <input style={inputEdit} type="date" {...register("data_emissao", { required: true })} />
            </div>
            <div>
              <label style={errors?.data_vencimento ? labelErrStyle : labelEditStyle}>Vencimento *</label>
              <input style={errors?.data_vencimento ? inputErr : inputEdit} type="date"
                {...register("data_vencimento", { required: "Data de vencimento é obrigatória" })} />
              {errors?.data_vencimento && <span style={errSpan}>⚠️ {errors.data_vencimento.message}</span>}
            </div>
            <div>
              <label style={watchedStatus !== "pago" ? { ...labelStyle, opacity: 0.5 } : labelEditStyle}>Pagamento</label>
              <input style={watchedStatus !== "pago" ? inputDis : inputEdit} type="date"
                {...register("data_pagamento")} disabled={watchedStatus !== "pago"} />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <div style={{ ...inputDis, display: "flex", alignItems: "center" }}>
                {watch("status") === "aberto" ? "Aberto" : watch("status") === "pago" ? "Pago" : watch("status") === "atrasado" ? "Atrasado" : "Cancelado"}
              </div>
            </div>
          </div>
        ) : (
          <div style={grid}>
            <ViewField label="Emissão" value={formatDateDisplay(watch("data_emissao"))} />
            <ViewField label="Vencimento" value={formatDateDisplay(watch("data_vencimento"))} />
            <ViewField label="Pagamento" value={formatDateDisplay(watch("data_pagamento"))} />
            <ViewField label="Status" value={
              <span style={{ padding: "3px 12px", borderRadius: 20, fontSize: "0.82rem", fontWeight: 700,
                background: watch("status") === "pago" ? "#d4edda" : watch("status") === "atrasado" ? "#f8d7da" : watch("status") === "cancelado" ? "#e2e3e5" : "#cfe2ff",
                color: watch("status") === "pago" ? "#155724" : watch("status") === "atrasado" ? "#b65f68" : watch("status") === "cancelado" ? "#495057" : "#084298" }}>
                {watch("status") === "aberto" ? "Aberto" : watch("status") === "pago" ? "Pago" : watch("status") === "atrasado" ? "Atrasado" : "Cancelado"}
              </span>
            } />
          </div>
        )}
      </div>

      <div style={divider} />

      {/* ── Seção 5: Observação ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isEdit ? (
          <div>
            <label style={labelEditStyle}>Observação</label>
            <textarea style={{ ...inputEdit, minHeight: 80, resize: "vertical" } as any}
              {...register("observacao")} placeholder="Informações adicionais..." />
          </div>
        ) : (
          <ViewField label="Observação" value={watch("observacao") || "Nenhuma observação."} />
        )}
      </div>

      {isEdit && (
        <div className={styles.buttonSave}>
          <Button variant="contain" type="submit" disabled={isPending || !isEdit}>
            {isPending ? "Salvando..." : "Salvar"}
          </Button>
          {uid && (
            <Button variant="contain" title="Cancelar edição"
              onClick={() => { handleVisible("isEdit", false); if (originalValue) { reset(originalValue); setValorBruto(originalValue.valor || 0); setValorDesconto(originalValue.desconto || 0); setValorJuros(originalValue.juros || 0); } }}
              typeStyle="cancel">
              Cancelar
            </Button>
          )}
        </div>
      )}
    </form>
  );
};

export default FormIncomingPayments;